import { TypedExtractor } from './interface';
// @ts-ignore
import { UnknownObj } from '@umu/umu-types';

export type UnknownType = string | number | object;

export const isObject = (obj: any) => {
    return Object.prototype.toString.call(obj) === '[object Object]';
};

export function isEmptyObject(obj?: UnknownObj): boolean {
    if (typeof obj !== 'object') {
        return true;
    }
    for (const name in obj) {
        if (name) {
            return false;
        }
    }
    return true;
}

// 复制对象
export function copyObj(srcObj: UnknownObj) {
    return JSON.parse(JSON.stringify(srcObj));
}

export const isSameObj = (obj1: UnknownObj, obj2: UnknownObj) => {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
};

/**
 * 剔除某些属性，获得新的 Object
 * @param object
 * @param omitProperty string[] | string
 * @returns object
 * @author Kody
 */
export const getOmitObj = (object: UnknownObj, omitProperty: string[] | string) => {
    const newObj: UnknownObj = {};
    for (const key in object) {
        if (!Object.prototype.hasOwnProperty.call(object, key)) {
            continue;
        }
        if (typeof omitProperty === 'string' && key === omitProperty) {
            continue;
        }
        if (Array.isArray(omitProperty) && omitProperty.indexOf(key) !== -1) {
            continue;
        }
        newObj[key] = object[key];
    }
    return newObj;
};

interface ISourceObject {
    [propName: string]: any;
}

/**
 * 深拷贝对象
 * @param source
 * @returns
 */
export function deepCopy<T>(source: T): T {
    if (!source || typeof source !== 'object') {
        return source;
    }
    if (source instanceof Array) {
        const copyArray: any[] = [];
        source.forEach((value) => {
            copyArray.push(deepCopy<any>(value));
        });
        return copyArray as any;
    }

    const copy: ISourceObject = {};
    if (source === Object(source)) {
        Object.keys(source).forEach((prop) => {
            copy[prop] = deepCopy<any>((source as ISourceObject)[prop]);
        });
    }
    return copy as any;
}

/**
 * @desc
 * @param
 */
export function classMapSet(classNames: { [name: string]: boolean } | string) {
    let names = '';

    if (typeof classNames === 'object') {
        for (const name in classNames) {
            if (!classNames.hasOwnProperty(name) || !classNames[name]) {
                continue;
            }
            names += name + ' ';
        }
    } else {
        for (let i = 0; i < arguments.length; i++) {
            // We should technically exclude 0 too, but for the sake of backward
            // compat we'll keep it (for now)
            if (arguments[i] == null) {
                continue;
            }
            names += arguments[i] + ' ';
        }
    }

    return names.trim();
}

/**
 * @description 根据指定key获取对象值，其中有一级别为空时即返回 undefinded , 这个source类型一定要写传主要解决可选值的类型推断。 不能被二次封装
 * 调用的地方source的类型一定要传。方法借鉴官方的object.assign, 参数最多只支持10个key.
 * @param  {object} source 数据
 * @param  {Array <string | number>} keys 对象值路径
 */
export const getData: TypedExtractor = (source: any, ...keys: Array<string | number>) => {
    let result: any = source;
    keys.forEach((key) => {
        if (result !== undefined && result !== null) {
            result = result[key];
        }
    });
    return result;
};

/**
 * 对比两个值是否相等, 弱类型比较
 * @param {string | number | object | array}
 * @return {boolean}
 */
export const isSame = (v1?: UnknownType, v2?: UnknownType): boolean => {
    if (v1 === v2) {
        return true;
    }

    if (!v1 || !v2) {
        return v1 === v2;
    }

    if (typeof v1 !== typeof v2) {
        return false;
    }

    if (Object.prototype.toString.call(v1) !== Object.prototype.toString.call(v2)) {
        return false;
    }

    if (isObject(v1) && isObject(v2)) {
        return isSameObj(v1 as UnknownObj, v2 as UnknownObj);
    }

    if (Array.isArray(v1) && Array.isArray(v2)) {
        if (v1.length !== v2.length) {
            return false;
        }
        return v1.every((item, i) => isSame(item, v2[i]));
    }
    return String(v1) === String(v2);
};

/**
 * @description 对比两个对象的指定key是否相等
 * @param  {object} obj1
 * @param  {object} obj2
 * @param  {Array <string | number>} keys
 * @return {boolean}
 */
export function compareObjects<T extends object, K extends keyof T>(
    obj1: T,
    obj2: T,
    keysToCompare: K[]
): boolean {
    return keysToCompare.every(key => obj1[key] === obj2[key]);
}
